import { sameTag, type ChatCard, type ChatTaggableKind } from './types'

/** The picker's type tabs. Every tab but All is also a chip in the composer, which opens the picker on it. */
export type ChatTagPickerTab =
	| 'all'
	| 'people'
	| 'flights'
	| 'accommodations'
	| 'activities'
	| 'ideas'
	| 'expenses'
	| 'payUps'

/** One row of the picker: the card it would tag, and who added it, for an idea. */
export interface ChatTagPickerItem {
	card: ChatCard
	byline: string | null
}

/** In the order the picker's tabs show. */
export const PICKER_TABS: readonly ChatTagPickerTab[] = [
	'all',
	'people',
	'flights',
	'accommodations',
	'activities',
	'ideas',
	'expenses',
	'payUps',
]

/** In the order the composer's chip row shows them. */
export const CHIP_TABS: readonly ChatTagPickerTab[] = [
	'flights',
	'accommodations',
	'activities',
	'ideas',
	'expenses',
	'payUps',
	'people',
]

const KIND: Record<Exclude<ChatTagPickerTab, 'all'>, ChatTaggableKind> = {
	people: 'Member',
	flights: 'FlightGroup',
	accommodations: 'Accommodation',
	activities: 'TripEvent',
	ideas: 'Idea',
	expenses: 'Expense',
	payUps: 'PayUp',
}

const TITLES: Record<ChatTagPickerTab, { tab: string; chip: string; sheet: string }> = {
	all: { tab: 'All', chip: 'All', sheet: 'Attach or tag' },
	people: { tab: 'People', chip: 'People', sheet: 'Tag a person' },
	flights: { tab: 'Flights', chip: 'Flight', sheet: 'Attach a flight' },
	accommodations: { tab: 'Accommodations', chip: 'Accommodation', sheet: 'Attach an accommodation' },
	activities: { tab: 'Activities', chip: 'Activity', sheet: 'Attach an activity' },
	ideas: { tab: 'Ideas', chip: 'Idea', sheet: 'Attach an idea' },
	expenses: { tab: 'Expenses', chip: 'Expense', sheet: 'Attach an expense' },
	payUps: { tab: 'Pay-ups', chip: 'Pay-up', sheet: 'Attach a pay-up' },
}

/** The kind a tab shows; null for All. */
export const tabKind = (tab: ChatTagPickerTab): ChatTaggableKind | null => (tab === 'all' ? null : KIND[tab])
export const tabTitle = (tab: ChatTagPickerTab): string => TITLES[tab].tab
export const chipTitle = (tab: ChatTagPickerTab): string => TITLES[tab].chip
export const sheetTitle = (tab: ChatTagPickerTab): string => TITLES[tab].sheet

/** Case- and accent-folded, so "cafe" finds "Café". */
const folded = (text: string): string => text.normalize('NFD').replace(/\p{Diacritic}/gu, '').toLowerCase()

/** A member is found by either name; anything else by its title. */
const searchText = (card: ChatCard): string => {
	const [person] = card.people
	return card.kind === 'Member' && person ? `${person.firstName} ${person.lastName}` : card.title
}

/**
 * The items on `tab` matching `query`, leaving out what's already tagged. All lists
 * people first, then each type in tab order; within a type, items keep their order. It
 * runs in the browser over trip data the app already holds: there is no search
 * endpoint. Ported from iOS `ChatTagPicker`.
 */
export const filterPickerItems = (
	items: readonly ChatTagPickerItem[],
	tab: ChatTagPickerTab,
	query: string,
	tagged: readonly ChatCard[],
): ChatTagPickerItem[] => {
	const needle = folded(query.trim())
	const own = tabKind(tab)
	const kinds = own ? [own] : PICKER_TABS.map(tabKind).filter((kind): kind is ChatTaggableKind => kind !== null)
	return kinds.flatMap(kind =>
		items.filter(
			({ card }) =>
				card.kind === kind &&
				!tagged.some(tag => sameTag(tag, card)) &&
				(needle === '' || folded(searchText(card)).includes(needle)),
		),
	)
}
